import { AxiosResponse } from 'axios';

import { SUBSCRIPTION_REQUEST_TIME_OUT } from '@/constants';
import { Organization } from '@/types/organization';
import {
  SubscriptionRequest,
  SubscriptionRequestWarnings,
} from '@/types/subscriptions/request';
import {
  Subscription,
  SubscriptionDetail,
} from '@/types/subscriptions/subscription';
import { api } from '@/services/api/axios/instance';

type GetListParams = {
  data: {
    organizationId: Organization['id'];
  };
  params: {
    per_page: number;
    service_group_id?: number;
    order_by?: string; // starts_on
  };
};

type GetParams = {
  data: {
    organizationId: Organization['id'];
    requestCode?: Subscription['requestCode'];
  };
};

type CreateParams = {
  data: {
    organizationId: Organization['id'];
    subscriptionRequest: SubscriptionRequest;
  };
  params: { dryrun: boolean };
};

type UpdateParams = {
  data: {
    organizationId: Organization['id'];
    subscriptionRequest: SubscriptionRequest;
    requestCode?: Subscription['requestCode'];
  };
  params: { dryrun: boolean };
};

type DeleteParams = {
  data: {
    organizationId: Organization['id'];
    requestCode?: Subscription['requestCode'];
  };
};

export const subscriptionRequestsApi = {
  getList: async (config: GetListParams): Promise<Subscription[]> => {
    const { organizationId } = config.data;
    const path = `organizations/${organizationId}/corporate_subscriptions`;

    const {
      data: { corporateSubscriptions: subscriptions },
    }: AxiosResponse<{
      corporateSubscriptions: Subscription[];
    }> = await api.get(path, config);

    return subscriptions;
  },
  get: async (config: GetParams): Promise<SubscriptionDetail> => {
    const { organizationId, requestCode } = config.data;
    const path = `organizations/${organizationId}/corporate_subscriptions/${requestCode}`;

    const {
      data: { corporateSubscription: subscription },
    }: AxiosResponse<{
      corporateSubscription: SubscriptionDetail;
    }> = await api.get(path, config);

    return subscription;
  },
  create: async (
    config: CreateParams,
  ): Promise<{
    subscription: SubscriptionDetail;
    warnings: SubscriptionRequestWarnings | null;
  }> => {
    const { organizationId, subscriptionRequest } = config.data;
    const { dryrun } = config.params;
    const path = `organizations/${organizationId}/corporate_subscriptions`;
    const data = {
      ...subscriptionRequest,
      dryrun,
    };

    const {
      data: { corporateSubscription: subscription, warnings },
    }: AxiosResponse<{
      corporateSubscription: SubscriptionDetail;
      warnings?: SubscriptionRequestWarnings;
    }> = await api.post(path, data, {
      timeout: SUBSCRIPTION_REQUEST_TIME_OUT,
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
      },
    });

    return {
      subscription,
      warnings: warnings || null,
    };
  },
  update: async (
    config: UpdateParams,
  ): Promise<{
    subscription: SubscriptionDetail;
    warnings: SubscriptionRequestWarnings | null;
  }> => {
    const { organizationId, subscriptionRequest, requestCode } = config.data;
    const { dryrun } = config.params;
    const path = `organizations/${organizationId}/corporate_subscriptions/${requestCode}`;
    const data = {
      ...subscriptionRequest,
      dryrun,
    };

    const {
      data: { corporateSubscription: subscription, warnings },
    }: AxiosResponse<{
      corporateSubscription: SubscriptionDetail;
      warnings?: SubscriptionRequestWarnings;
    }> = await api.patch(path, data, {
      timeout: SUBSCRIPTION_REQUEST_TIME_OUT,
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
      },
    });

    return {
      subscription,
      warnings: warnings || null,
    };
  },
  delete: async (config: DeleteParams): Promise<void> => {
    const { organizationId, requestCode } = config.data;
    const path = `organizations/${organizationId}/corporate_subscriptions/${requestCode}`;

    await api.delete(path, config);
  },
};
